import Icon, { Icons } from '@/presentation/components/elements/Icon';
import Select from '@/presentation/components/elements/Select';
import FormattedEther from '@/components/elements/FormattedEther';
import VaultIcon from '@/presentation/components/elements/VaultIcon';
import {
  Address,
  Asset,
  LiquidityPool,
  Vault,
  VaultActivity,
  Zero,
} from 'nftx.js';
import { QueryStatus } from '@tanstack/react-query';
import { useFormatter, useTranslations } from 'use-intl';
import { Chain, formatEther } from 'viem';
import Collapsible from '@/presentation/components/modules/Collapsible';
import AssetImage from '@/presentation/components/elements/AssetImage';
import { DailySnapshot, HourlySnapshot } from '@/core/tokens';
import MultiPriceChart from '@/presentation/components/charts/MultiPriceChart';
import Price from '@/presentation/components/elements/Price';
import { useMemo } from 'react';
import ellipsis from '@/presentation/utils/ellipsis';
import { enDash } from '@/presentation/utils/text';
import Tooltip from '@/presentation/components/ui/Tooltip';
import PoolsTable from '@/presentation/components/tables/PoolsTable';
import TopStaker from './TopStaker';
import VaultSwitcher from '@/presentation/components/modules/VaultSwitcher';

const Info = ({
  chain,
  vaults,
  onSelectVault,
  selectedVault,
  activity,
  activityStatus,
  assets,
  assetsStatus,
  snapshotsStatus,
  snapshots,
  pools,
  poolsStatus,
  totalEth,
  totalVToken,
  tvl,
  imageUrl,
  topStakers,
}: {
  imageUrl?: string;
  chain: Chain;
  activity?: VaultActivity[];
  activityStatus: QueryStatus;
  vaults?: Vault[];
  selectedVault: Vault | undefined;
  onSelectVault: (vault: Vault | undefined) => void;
  assets?: Asset[];
  assetsStatus: QueryStatus;
  snapshots?: {
    dailySnapshots: DailySnapshot[];
    hourlySnapshots: HourlySnapshot[];
    vaultId: string;
    tokenAddress: Address;
  }[];
  snapshotsStatus: QueryStatus;
  pools?: LiquidityPool[];
  poolsStatus: QueryStatus;
  totalEth: bigint;
  totalVToken: bigint;
  tvl: bigint;
  topStakers: { userAddress: Address; poolShare: bigint; value: bigint }[];
}) => {
  const t = useTranslations();
  const now = Date.now();
  const hasMultipleVaults = vaults && vaults.length > 1;
  const { relativeTime, number } = useFormatter();

  const fees7d = useMemo(() => {
    let relevantPools = pools ?? [];
    if (selectedVault) {
      relevantPools = relevantPools.filter(
        (pool) => pool.vaultId === selectedVault.vaultId
      );
    }
    return relevantPools.reduce((total, pool) => {
      return total + pool.weeklyRevenue;
    }, Zero);
  }, [pools, selectedVault]);

  const volume7d = useMemo(() => {
    let relevantPools = pools ?? [];
    if (selectedVault) {
      relevantPools = relevantPools.filter(
        (pool) => pool.vaultId === selectedVault.vaultId
      );
    }
    return relevantPools.reduce((total, pool) => {
      return total + pool.weeklyVolume;
    }, Zero);
  }, [pools, selectedVault]);

  const apr7d = useMemo(() => {
    if (tvl === Zero) return 0;
    if (fees7d === Zero) return 0;
    const annualizedFees = Number(fees7d) * 52;
    const apr = (annualizedFees * 100) / Number(tvl);
    return apr;
  }, [fees7d, tvl]);

  return (
    <div className="space-y-4">
      <header className="flex items-center">
        {hasMultipleVaults && (
          <VaultSwitcher
            vaults={vaults}
            selected={selectedVault}
            setSelected={onSelectVault}
            showAddress={false}
          />
        )}
      </header>
      <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <div className="p-4 rounded-lg border border-gray-200 bg-gray-50 lg:col-span-1 order-1">
          <div className="bg-gray-200 border border-gray-300 rounded-md p-4 mb-4">
            <h4 className="text-gray-500 text-sm mb-4">
              {'Agg. Tokens Locked'}
            </h4>
            <dl className="space-y-2">
              <div className="flex">
                <dt className="mr-4">
                  <Icon name={Icons.ETH} size={24} />
                </dt>
                <dd>
                  <FormattedEther value={totalEth} />
                </dd>
              </div>
              <div className="flex">
                <dt className="mr-4">
                  {selectedVault ? (
                    <VaultIcon
                      className="w-6"
                      vaultId={selectedVault.vaultId}
                    />
                  ) : (
                    <img
                      src={imageUrl}
                      alt={vaults?.[0].asset.name}
                      className="w-6"
                    />
                  )}
                </dt>
                <dd>
                  <FormattedEther value={totalVToken} />
                </dd>
              </div>
            </dl>
          </div>
          <dl className="space-y-2">
            <div>
              <dt className="text-base text-gray-500 font-medium">{'TVL'}</dt>
              <dd className="text-lg font-bold">
                <Price value={tvl} />
              </dd>
            </div>
            <div>
              <dt className="text-base text-gray-500 font-medium">
                {'7d Volume'}
              </dt>
              <dd className="text-lg font-bold">
                <Price value={volume7d} />
              </dd>
            </div>
            <div>
              <dt className="text-base text-gray-500 font-medium">
                {'7d Fees'}
              </dt>
              <dd className="text-lg font-bold">
                <Price value={fees7d} />
              </dd>
            </div>
            <div>
              <dt className="text-base text-gray-500 font-medium">
                {'7d APR'}
              </dt>
              <dd className="text-lg font-bold">{apr7d.toFixed(2)}%</dd>
            </div>
          </dl>
        </div>
        <div className="p-6 rounded-lg border border-gray-200 bg-gray-50 md:col-span-2 order-2 md:order-3 lg:order-2">
          <h4 className="text-base font-bold text-gray-800 mb-4">
            {'Price History'}
          </h4>
          <div className="relative min-h-[200px]">
            {snapshotsStatus === 'loading' && (
              <div className="text-center p-2">
                <Icon
                  name={Icons.SPINNER_GAP}
                  className="animate-spin inline-flex"
                  size={24}
                />
              </div>
            )}
            {snapshotsStatus === 'success' && !snapshots?.length && (
              <p className="text-sm font-medium text-center text-gray-500">
                {'No price data'}
              </p>
            )}
            {snapshotsStatus === 'success' && !!snapshots?.length && (
              <MultiPriceChart snapshots={snapshots} />
            )}
          </div>
        </div>
        <div className="rounded-lg border border-gray-200 bg-gray-50 lg:col-span-1 order-3 md:order-2 lg:order-3">
          {selectedVault ? (
            <div className="p-4">
              <h4 className="text-base font-bold text-gray-800 mb-4">
                {'Details'}
              </h4>
              <dl className="space-y-2 text-sm">
                {(
                  [
                    { key: 'name', value: selectedVault.token.name },
                    {
                      key: 'address',
                      value: (
                        <a
                          href={`
                            ${chain.blockExplorers?.default.url}/address/${selectedVault.token.id}
                          `}
                          target="_blank"
                          rel="nofollow noreferrer"
                          className="underline hover:no-underline"
                        >
                          {ellipsis(selectedVault.token.id)}
                        </a>
                      ),
                    },
                    {
                      key: 'symbol',
                      value: selectedVault.token.symbol,
                    },
                    { key: 'icon', value: selectedVault.vaultId },
                  ] as const
                ).map((x) => (
                  <div className="flex justify-between" key={x.key}>
                    <dt className="font-medium text-gray-800/75">
                      {t(`CreateVault.Summary.vault.${x.key}`)}
                    </dt>
                    <dd className="text-right text-gray-500">
                      {x.key === 'icon' ? (
                        <VaultIcon vaultId={x.value} className="w-8 h-8" />
                      ) : (
                        x.value || '--'
                      )}
                    </dd>
                  </div>
                ))}
                <div className="flex justify-between">
                  <dt className="font-medium text-gray-800/75">
                    {t('CreateVault.Summary.vault.features')}
                  </dt>
                  <dd className="text-right text-gray-500 space-y-1">
                    {Object.entries(selectedVault.features).map(
                      ([key, val]) => {
                        return (
                          <span
                            key={key}
                            className="flex justify-end space-x-2 items-center"
                          >
                            <span>
                              {t(`CreateVault.Summary.vault.feature.${key}`)}
                            </span>
                            {val ? (
                              <Icon
                                name={Icons.CHECK_CIRCLE}
                                size={16}
                                className="text-green-500"
                              />
                            ) : (
                              <Icon
                                name={Icons.X_MARK}
                                size={16}
                                className="text-red-500"
                              />
                            )}
                          </span>
                        );
                      }
                    )}
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt className="font-medium text-gray-800/75">
                    {t('CreateVault.Summary.vault.fees')}
                  </dt>
                  <dd className="text-right text-gray-500 space-y-1">
                    {Object.entries(selectedVault.fees).map(([key, val]) => (
                      <span
                        key={key}
                        className="flex justify-end space-x-2 items-center"
                      >
                        <span>{t(`CreateVault.Summary.fees.${key}`)}</span>
                        <span>
                          {number(Number(formatEther(val)), {
                            style: 'percent',
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 2,
                          })}
                        </span>
                      </span>
                    ))}
                  </dd>
                </div>
                {!!selectedVault.eligibilityModule?.id && (
                  <div className="flex justify-between">
                    <dt className="font-medium text-gray-800/75">
                      {t('CreateVault.Summary.vault.eligibility.title')}
                    </dt>
                    <dd className="text-right text-gray-500 flex-1 space-y-1 pl-2">
                      <div>
                        {!!selectedVault.eligibilityModule.eligibleIds
                          ?.length && 'ID List'}
                        {!!selectedVault.eligibilityModule.eligibleRange
                          ?.length && 'ID Range'}
                      </div>
                      {!!selectedVault.eligibilityModule.eligibleRange
                        ?.length && (
                        <div>
                          {selectedVault.eligibilityModule?.eligibleRange?.join(
                            ` ${enDash} `
                          )}
                        </div>
                      )}
                      {selectedVault.eligibilityModule.eligibleIds && (
                        <div className="flex-1 rounded-md max-h-40 overflow-y-scroll hide-scroll p-1 bg-gray-100 border border-gray-200">
                          {selectedVault.eligibilityModule.eligibleIds?.join(
                            ', '
                          )}
                        </div>
                      )}
                      {/* <a
                        href={`${chain.blockExplorers?.default.url}/address/${selectedVault.eligibilityModule.id}`}
                        target="_blank"
                        rel="noreferrer nofollow"
                        className="underline hover:no-underline text-gray-500 inline-block"
                      >
                        {ellipsis(selectedVault.eligibilityModule.id)}
                      </a> */}
                    </dd>
                  </div>
                )}
              </dl>
            </div>
          ) : (
            <>
              <h4 className="text-base font-bold text-gray-800 px-4 pt-4">
                {'Vaults'}
              </h4>
              <ul className="space-y-2 py-4">
                {vaults?.map((v) => {
                  return (
                    <li key={v.vaultId}>
                      <button
                        className="flex items-center hover:bg-gray-100 w-full text-left px-4 py-1"
                        type="button"
                        onClick={() => onSelectVault(v)}
                      >
                        <VaultIcon
                          vaultId={v.vaultId}
                          className="w-8 h-8 mr-4"
                        />
                        <hgroup className="text-sm">
                          <h5 className="font-medium">
                            {v.token.name}
                            {v?.eligibilityModule?.id && (
                              <Tooltip
                                label={
                                  <span className="ml-2">
                                    <Icon
                                      name={Icons.TICKET}
                                      size={20}
                                      className="inline-flex my-auto text-pink-500"
                                    />
                                  </span>
                                }
                                content={
                                  'This vault only accepts specific items from this collection.'
                                }
                              />
                            )}
                          </h5>
                          <h6 className="text-xs text-gray-500">
                            {v.token.symbol}
                            {` (#${v.vaultId})`}
                          </h6>
                        </hgroup>
                        <div className="ml-auto pl-4 space-x-4 text-sm text-right">
                          <div>
                            {v.totalHoldings} {'items'}
                          </div>
                          <Price value={v.prices[0].redeem.price} />
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </div>
        <div className="p-4 rounded-lg border border-gray-200 bg-gray-50 lg:col-span-1 order-4">
          <h4 className="text-base font-bold text-gray-800">
            {'Recent Activity'}
          </h4>

          <table className="w-full mt-4">
            <thead className="text-xs font-black uppercase">
              <tr>
                <th className="text-left p-2">{'Pool'}</th>
                <th className="p-2"></th>
                <th className="text-right p-2">{'Fee'}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {activityStatus === 'loading' && (
                <tr>
                  <td colSpan={2} className="p-2 text-center">
                    <Icon
                      name={Icons.SPINNER_GAP}
                      className="animate-spin inline-flex"
                      size={24}
                    />
                  </td>
                </tr>
              )}
              {activityStatus === 'success' &&
                !!activity?.length &&
                activity?.map((a) => (
                  <tr key={a.txId}>
                    <td className="p-2">
                      <div className="flex items-center space-x-2 w-full">
                        <VaultIcon vaultId={a.vaultId} className="w-6" />
                        <span className="bg-gray-500 text-gray-50 font-black text-xxs uppercase p-1 rounded-sm">
                          {a.type}
                        </span>
                        <span className="text-sm font-bold">
                          {(a.type === 'buy' ||
                            a.type === 'sell' ||
                            a.type === 'swap' ||
                            a.type === 'mint' ||
                            a.type === 'redeem') &&
                            a?.tokenIds?.length > 1 &&
                            `×${a.tokenIds.length}`}
                        </span>
                      </div>
                    </td>
                    <td className="text-right text-xxs font-bold text-gray-400">
                      <a
                        href={`${chain.blockExplorers?.default.url}/tx/${a.txId}`}
                        target="_blank"
                        rel="nofollow noreferrer"
                        className="hover:underline"
                      >
                        {relativeTime(a.date * 1000, now)}
                      </a>
                    </td>
                    <td className="p-2 text-right text-sm">
                      <FormattedEther
                        value={
                          ((a.type === 'buy' ||
                            a.type === 'sell' ||
                            a.type === 'swap' ||
                            a.type === 'mint' ||
                            a.type === 'redeem') &&
                            a?.feeAmount) ||
                          0n
                        }
                        maximumFractionDigits={6}
                      />
                      {' ETH'}
                    </td>
                  </tr>
                ))}
              {activityStatus === 'success' && !activity?.length && (
                <tr>
                  <td colSpan={2} className="p-2 text-sm text-gray-500">
                    {'No activity'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="p-4 rounded-lg border border-gray-200 bg-gray-50 lg:col-span-1 order-5">
          <h4 className="text-base font-bold text-gray-800">Top Stakers</h4>
          <table className="w-full mt-4">
            <thead className="text-xs font-black uppercase">
              <tr>
                <th className="p-2 text-left">Address</th>
                <th className="p-2 text-right">Position value</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {topStakers.map((staker) => (
                <TopStaker key={staker.userAddress} {...staker} />
              ))}
            </tbody>
          </table>
        </div>
        <div className="md:col-span-2 lg:col-span-3 order-6 overflow-hidden">
          <Collapsible
            open={true}
            key={pools?.length}
            header={
              <h4 className="text-base font-bold text-gray-800">
                {`Pools (${pools?.length || 0})`}
              </h4>
            }
          >
            <PoolsTable pools={pools} status={poolsStatus} border={false} />
          </Collapsible>
        </div>
        <div className="md:col-span-2 lg:col-span-3 order-7">
          <Collapsible
            open={true}
            key={assets?.length}
            header={
              <h4 className="text-base font-bold text-gray-800">
                {`Underlying items (${assets?.length || 0})`}
              </h4>
            }
          >
            {assetsStatus === 'loading' && (
              <div className="p-2 text-center">
                <Icon
                  name={Icons.SPINNER_GAP}
                  className="animate-spin inline-flex"
                  size={24}
                />
              </div>
            )}
            {assetsStatus === 'success' && !!assets?.length && (
              <div className="flex flex-wrap gap-2 p-4">
                {assets.map((a) => (
                  <AssetImage
                    className="w-16 h-16"
                    assetAddress={a.assetAddress}
                    tokenId={a.tokenId}
                    imageUrl={a.imageUrl}
                    key={a.id}
                  />
                ))}
              </div>
            )}
            {assetsStatus === 'success' && !assets?.length && (
              <div className="p-4 text-sm text-gray-500 text-center">
                {'No assets'}
              </div>
            )}
          </Collapsible>
        </div>
      </section>
    </div>
  );
};

export default Info;
